import apiClient from '@/api/client';

// ─────── INTERFACES ─────────

export interface RatingScaleLevel {
  value: number;
  label: string;
  description?: string;
  color?: string;
}

export interface RatingScale {
  id: string;
  company_id: string;
  name: string;
  description?: string;
  scale_type: string;
  min_value: number;
  max_value: number;
  levels: RatingScaleLevel[];
  is_default: boolean;
  created_at: string;
}

export interface ReviewTemplate {
  id: string;
  company_id: string;
  name: string;
  description?: string;
  template_config: Record<string, unknown>;
  rating_scale_id?: string;
  rating_scale?: RatingScale;
  is_active: boolean;
  created_at: string;
}

export interface ReviewCycle {
  id: string;
  company_id: string;
  name: string;
  description?: string;
  cycle_type: string;
  start_date: string;
  end_date: string;
  self_review_deadline?: string;
  peer_review_deadline?: string;
  manager_review_deadline?: string;
  status: string;
  template_id?: string;
  rating_scale_id?: string;
  settings: Record<string, unknown>;
  created_at: string;
}

export interface ReviewCycleDetail extends ReviewCycle {
  template?: ReviewTemplate;
  rating_scale?: RatingScale;
  total_assignments: number;
  completed_assignments: number;
  completion_pct: number;
}

export interface Goal {
  id: string;
  company_id: string;
  employee_id: string;
  cycle_id?: string;
  title: string;
  description?: string;
  goal_type: string;
  weight: number;
  target_value?: number;
  current_value?: number;
  unit?: string;
  status: string;
  priority: string;
  due_date?: string;
  completed_at?: string;
  tags: string[];
  bsc_perspective?: string | null;
  check_ins?: GoalCheckIn[];
  created_at: string;
}

export interface BSCQuadrant {
  total_goals: number;
  completed: number;
  completion_rate: number;
  avg_score: number;
}

export interface BSCScorecard {
  financial: BSCQuadrant;
  customer: BSCQuadrant;
  internal_process: BSCQuadrant;
  learning_growth: BSCQuadrant;
}

export interface GoalCheckIn {
  id: string;
  goal_id: string;
  author_id: string;
  progress_pct: number;
  previous_value?: number;
  new_value?: number;
  notes?: string;
  created_at: string;
}

export interface ReviewAssignment {
  id: string;
  cycle_id: string;
  employee_id: string;
  reviewer_id: string;
  reviewer_type: string;
  status: string;
  due_date?: string;
  submitted_at?: string;
  is_anonymous: boolean;
  created_at: string;
  employee_name?: string;
  reviewer_name?: string;
  responses?: ReviewResponseItem[];
}

export interface ReviewResponseItem {
  id: string;
  assignment_id: string;
  section_key: string;
  competency_key?: string;
  rating?: number;
  comments?: string;
  created_at: string;
}

export interface ReviewSummary {
  id: string;
  cycle_id: string;
  employee_id: string;
  overall_rating?: number;
  goal_score?: number;
  competency_score?: number;
  section_scores: Record<string, number>;
  manager_comments?: string;
  strengths?: string;
  development_areas?: string;
  employee_acknowledgment: boolean;
  acknowledged_at?: string;
  employee_comments?: string;
  status: string;
  rating_label?: string;
  recommendations: Record<string, unknown>;
  created_at: string;
  employee_name?: string;
}

export interface CycleAnalytics {
  total_employees: number;
  total_assignments: number;
  completed: number;
  pending: number;
  in_progress: number;
  completion_pct: number;
  avg_rating?: number;
  rating_distribution: { label: string; count: number; percentage: number }[];
  by_department: { department_id?: string; avg_rating?: number; count: number }[];
}

export interface GoalAnalytics {
  total_goals: number;
  not_started: number;
  in_progress: number;
  completed: number;
  exceeded: number;
  cancelled: number;
  completion_rate: number;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages?: number;
}

// ─────── API ─────────

export const ratingScalesApi = {
  list: () =>
    apiClient.get<RatingScale[]>('/performance/rating-scales'),
  create: (data: Partial<RatingScale>) =>
    apiClient.post<RatingScale>('/performance/rating-scales', data),
  update: (id: string, data: Partial<RatingScale>) =>
    apiClient.patch<RatingScale>(`/performance/rating-scales/${id}`, data),
};

export const reviewTemplatesApi = {
  list: (params?: { active_only?: boolean }) =>
    apiClient.get<ReviewTemplate[]>('/performance/templates', { params }),
  get: (id: string) =>
    apiClient.get<ReviewTemplate>(`/performance/templates/${id}`),
  create: (data: Partial<ReviewTemplate>) =>
    apiClient.post<ReviewTemplate>('/performance/templates', data),
  update: (id: string, data: Partial<ReviewTemplate>) =>
    apiClient.patch<ReviewTemplate>(`/performance/templates/${id}`, data),
};

export const reviewCyclesApi = {
  list: (params?: { status?: string; year?: number; page?: number; per_page?: number }) =>
    apiClient.get<PaginatedResponse<ReviewCycle>>('/performance/cycles', { params }),
  get: (id: string) =>
    apiClient.get<ReviewCycleDetail>(`/performance/cycles/${id}`),
  create: (data: Partial<ReviewCycle>) =>
    apiClient.post<ReviewCycle>('/performance/cycles', data),
  update: (id: string, data: Partial<ReviewCycle>) =>
    apiClient.patch<ReviewCycle>(`/performance/cycles/${id}`, data),
  launch: (id: string) =>
    apiClient.post(`/performance/cycles/${id}/launch`),
  close: (id: string) =>
    apiClient.post(`/performance/cycles/${id}/close`),
  assignments: (cycleId: string, params?: { employee_id?: string; status?: string }) =>
    apiClient.get<ReviewAssignment[]>(`/performance/cycles/${cycleId}/assignments`, { params }),
  createAssignment: (cycleId: string, data: { employee_id: string; reviewer_id: string; reviewer_type: string; due_date?: string; is_anonymous?: boolean }) =>
    apiClient.post<ReviewAssignment>(`/performance/cycles/${cycleId}/assignments`, data),
  summaries: (cycleId: string) =>
    apiClient.get<ReviewSummary[]>(`/performance/cycles/${cycleId}/summaries`),
  getSummary: (cycleId: string, employeeId: string) =>
    apiClient.get<ReviewSummary>(`/performance/cycles/${cycleId}/employees/${employeeId}/summary`),
  updateSummary: (cycleId: string, employeeId: string, data: Partial<ReviewSummary>) =>
    apiClient.patch<ReviewSummary>(`/performance/cycles/${cycleId}/employees/${employeeId}/summary`, data),
  acknowledge: (cycleId: string, employeeId: string, data: { comments?: string }) =>
    apiClient.post(`/performance/cycles/${cycleId}/employees/${employeeId}/acknowledge`, data),
  analytics: (cycleId: string) =>
    apiClient.get<CycleAnalytics>(`/performance/analytics/cycle/${cycleId}`),
};

export const goalsApi = {
  list: (params?: { employee_id?: string; cycle_id?: string; status?: string; bsc_perspective?: string; page?: number; per_page?: number }) =>
    apiClient.get<PaginatedResponse<Goal>>('/performance/goals', { params }),
  bscScorecard: (cycleId?: string) =>
    apiClient.get<BSCScorecard>('/performance/analytics/bsc', { params: cycleId ? { cycle_id: cycleId } : {} }),
  get: (id: string) =>
    apiClient.get<Goal>(`/performance/goals/${id}`),
  create: (data: Partial<Goal> & { employee_id: string }) =>
    apiClient.post<Goal>('/performance/goals', data),
  update: (id: string, data: Partial<Goal>) =>
    apiClient.patch<Goal>(`/performance/goals/${id}`, data),
  checkIn: (goalId: string, data: { progress_pct: number; new_value?: number; notes?: string }) =>
    apiClient.post<GoalCheckIn>(`/performance/goals/${goalId}/check-in`, data),
  employeeGoals: (employeeId: string, params?: { cycle_id?: string; status?: string }) =>
    apiClient.get<PaginatedResponse<Goal>>(`/performance/employees/${employeeId}/goals`, { params }),
  myGoals: (params?: { cycle_id?: string; status?: string }) =>
    apiClient.get<PaginatedResponse<Goal>>('/performance/my-goals', { params }),
  analytics: (params?: { cycle_id?: string }) =>
    apiClient.get<GoalAnalytics>('/performance/analytics/goals', { params }),
};

export const reviewsApi = {
  myReviews: () =>
    apiClient.get<{ items: ReviewAssignment[] }>('/performance/my-reviews'),
  getAssignment: (id: string) =>
    apiClient.get<ReviewAssignment>(`/performance/assignments/${id}`),
  submit: (assignmentId: string, data: { responses: { section_key: string; competency_key?: string; rating?: number; comments?: string }[]; overall_comments?: string }) =>
    apiClient.post(`/performance/assignments/${assignmentId}/submit`, data),
};
